import {
  isTraversalLoadedState,
  type LoadedState,
  type Path,
  type TraversalLoadedState,
} from '@ardoq/api-types';

/**
 * Whether there is a triple anywhere in `paths`. The array may hold a single
 * empty path rather than being empty itself, which carries just as little
 * information — `decodeTraversal` decodes an empty paths slot that way.
 */
export const hasTraversalPaths = (paths: Path[]): boolean =>
  paths.some(path => path.length > 0);

/**
 * A start-set expansion also holds the sentinel as its paths' start endpoint.
 * That is not a wildcard start set as long as the type it expands from is known,
 * which is the same condition the saved `startType` is taken from - without it
 * there is nothing but the sentinel to store.
 */
export const hasNamedExpansion = (
  data: TraversalLoadedState['data']
): boolean => Boolean(data.startSetExpansion && data.startSetExpansionFromType);

/**
 * A traversal whose `paths` hold at least one triple.
 *
 * The non-empty tuple is what keeps {@link isTraversalWithPaths} sound in both
 * directions: a plain `TraversalLoadedState` is not assignable to it, so the
 * compiler keeps pathless traversals in the negative branch instead of typing
 * them out of it. It is deliberately weaker than the predicate — the predicate
 * also rejects `[[]]`, which this type admits — because a type cannot express
 * "some path is non-empty" and an under-promise is the safe direction.
 */
type TraversalLoadedStateWithPaths = TraversalLoadedState & {
  data: { paths: [Path, ...Path[]] };
};

/**
 * A traversal with no triples in its paths carries no type information in its
 * definition, so consumers that read types out of `data.paths` have to fall back
 * to the loaded start set instead. Such traversals are produced by opening a
 * non-graph view from inventory — see
 * `componentSearchRoutines.getParamsAndRequestPromise`.
 */
export const isTraversalWithPaths = (
  state: LoadedState
): state is TraversalLoadedStateWithPaths =>
  isTraversalLoadedState(state) && hasTraversalPaths(state.data.paths);
